import { generateSubtopicsDeepseek, generateSubtopicsGemini, generateSubtopicsClaude } from './generateSubtopics';
import { GeminiClient } from './GeminiClient';
import ActorCriticAnalysis from './ActorCriticAnalysis';

/**
 * Helper function to "patch or append" in chatSections:
 *   1) If newObj has exactly one key: 'subtopics', create a new element.
 *   2) Otherwise, patch the last element with the new keys (keysToAdd).
 * 
 * Additionally, we log each time subtopics is set to see if it happens twice.
 */
function patchOrAppendChatSections(prevChats, newObj, keysToAdd) {
  // If no chatSections yet, create the first element
  if (prevChats.length === 0) {
    try {
      localStorage.setItem('isEnabled', 'false');
    } catch (error) {
      // console.error('Error setting localStorage:', error);
      // console.error('localStorage error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
    }
    return [newObj];
  }

  let isEnabled = 'false';
  try {
    isEnabled = localStorage.getItem('isEnabled');
  } catch (error) {
    // console.error('Error getting localStorage:', error);
    // console.error('localStorage get error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
  }

  // If localStorage 'isEnabled' is true => create new element
  if (isEnabled === 'true') {
    const updated = [...prevChats];
    updated.push(newObj);
    try {
      localStorage.setItem('isEnabled', 'false');
    } catch (error) {
      // console.error('Error setting localStorage after push:', error);
      // console.error('localStorage set error stack:', error.stack);
    }
    return updated;
  }

  // Otherwise, patch the last element in the array
  const updated = [...prevChats];
  const lastIndex = updated.length - 1;
  const lastElem = updated[lastIndex];
  updated[lastIndex] = { ...lastElem, ...keysToAdd };
  return updated;
}

export const mainWorkflow = async (
  Searchweb, setLoadingarticles, setErrorarticles, DeepSearch, Think, input, first_img, setThinkingobj, setSubtopicshook,
  articleshook, setArticleshook, setFinalSummaryhook, setIter1Hook, chatSections, setChatSections, thinkingobj,
  signal, setIsLoading,
  apiError, setAPIError,
  Actor_Model_apiProvider, setActor_Model_apiProvider,
  Critic_Model_apiProvider, setCritic_Model_apiProvider,
  Actor_Model_apiModel, setActor_Model_apiModel,
  Critic_Model_apiModel, setCritic_Model_apiModel,
  Actor_Model_apikey, setActor_Model_apikey,
  Critic_Model_apikey, setCritic_Model_apikey,
  temperature_val_Actor_Model_apiProvider, setTemperature_val_Actor_Model_apiProvider,
  temperature_val_Critic_Model_apiProvider, setTemperature_val_Critic_Model_apiProvider
) => {
  let fetchedArticles = []; // Declare outside to make it accessible after the conditional block

  try {
    // console.log('[mainWorkflow] Starting workflow execution with input:', input);
    // console.log('[mainWorkflow] Selected model provider:', first_img);

    // Check for abort signal before starting
    if (signal?.aborted) {
      // console.error('[mainWorkflow] Workflow aborted before initialization');
      setThinkingobj((prevState) => {
        const newObj = { ...prevState, error: true, loading: false, timout_err: true };
        setChatSections((prev) => {
          return patchOrAppendChatSections(prev, newObj, { error: true, loading: false, timout_err: true });
        });
        return newObj;
      });
      throw new Error('Workflow aborted');
    }

    // Update thinkingobj with initial state (without subtopics, since it's not defined yet)
    setThinkingobj((prevState) => {
      const newObj = { ...prevState, stage: 'Started Thinking...', loading: true, timout_err: false };
      setChatSections((prevChats) => {
        return patchOrAppendChatSections(prevChats, newObj, { stage: 'Started Thinking...', loading: true, timout_err: false });
      });
      return newObj;
    });

    let loopCount = 0;
    try {
      loopCount = parseInt(localStorage.getItem('loops') || '0');
      // console.log('[mainWorkflow] Retrieved loop count from localStorage:', loopCount);
    } catch (error) {
      // console.error('Error getting loop count from localStorage:', error);
      // console.error('Loop count error details:', error.message);
      // console.error('Loop count error stack:', error.stack);
    }

    // 1) Generate subtopics EXACTLY once here
    let subtopics;
    if (signal?.aborted) {
      // console.error('[mainWorkflow] Workflow aborted before subtopics generation');
      throw new Error('Workflow aborted before subtopics generation');
    }
    // console.log('[mainWorkflow] Generating subtopics with provider:', first_img);

    try {
      if (first_img === 'Deepseek') {
        // console.log('[mainWorkflow] Calling generateSubtopicsDeepseek with Actor_Model_apiKey length:', Actor_Model_apikey ? Actor_Model_apikey.length : 0);
        subtopics = await generateSubtopicsDeepseek(Think, input, setAPIError, signal, Actor_Model_apiProvider, Actor_Model_apiModel, Actor_Model_apikey, temperature_val_Actor_Model_apiProvider);
      } else if (first_img === 'Google Gemini') {
        // console.log('[mainWorkflow] Calling generateSubtopicsGemini with Actor_Model_apiKey length:', Actor_Model_apikey ? Actor_Model_apikey.length : 0);
        subtopics = await generateSubtopicsGemini(Think, input, setAPIError, signal, Actor_Model_apiModel, Actor_Model_apikey, temperature_val_Actor_Model_apiProvider);
      } else if (first_img === 'Anthropic') {
        // console.log('[mainWorkflow] Calling generateSubtopicsClaude with Actor_Model_apiKey length:', Actor_Model_apikey ? Actor_Model_apikey.length : 0);
        subtopics = await generateSubtopicsClaude(Think, input, setAPIError, signal, Actor_Model_apiProvider, Actor_Model_apiModel, Actor_Model_apikey, temperature_val_Actor_Model_apiProvider);
      } else {
        // console.error('[mainWorkflow] Unknown provider:', first_img);
        throw new Error(`Unknown provider: ${first_img}`);
      }
      // console.log('[mainWorkflow] Successfully generated subtopics:', subtopics ? `${subtopics.substring(0, 100)}...` : 'undefined');
    } catch (error) {
      // console.error('[mainWorkflow] Failed to generate subtopics:', error);
      // console.error('[mainWorkflow] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      throw error; // Re-throw to be caught by the outer try-catch
    }

    if (signal?.aborted) {
      // console.error('[mainWorkflow] Signal aborted after subtopics generation');
      setThinkingobj((prevState) => {
        const newObj = { ...prevState, error: true, loading: false, timout_err: true };
        setChatSections((prev) => {
          return patchOrAppendChatSections(prev, newObj, { error: true, loading: false, timout_err: true });
        });
        return newObj;
      });
      throw new Error('Subtopics generation aborted');
    }

    // Update thinkingobj with subtopics
    setThinkingobj((prevState) => {
      const newObj = { ...prevState, stage: 'Ended Thinking', subtopics: subtopics, loading: true, timout_err: false };
      setChatSections((prevChats) => {
        return patchOrAppendChatSections(prevChats, newObj, { stage: 'Ended Thinking', subtopics: subtopics, loading: true, timout_err: false });
      });
      return newObj;
    });

    setSubtopicshook(subtopics);
    // console.log('subtopics', subtopics)
    if (!subtopics) {
      console.error('[mainWorkflow] No subtopics generated, ending workflow early');
      setIsLoading(false);
      return;
    }

    // Conditional article fetching using the flag
    if (Think && Searchweb && subtopics) {
      // console.log('think true=>', Think)
      setThinkingobj((prevState) => {
        const newObj = { ...prevState, stage: 'Started Mining Web...', subtopics: subtopics, loading: true, timout_err: false };
        setChatSections((prevChats) => {
          return patchOrAppendChatSections(prevChats, newObj, { stage: 'Started Mining Web...', subtopics: subtopics, loading: true, timout_err: false });
        });
        return newObj;
      });
      // 2) Fetch articles via proxy
      const fetchArticles = async () => {
        // Parse subtopics from the string, split by comma, and trim whitespace
        const allTopicsStr = subtopics;
        const allTopics = allTopicsStr.split('#').map(topic => topic.trim()).filter(topic => topic.length > 0).slice(3, allTopicsStr?.length)
        const allTopicstrimmed = allTopics.map(topic => {
          const trimmedTopic = topic.trim();
          const words = trimmedTopic.split(/\s+/); // Split by whitespace
          if (words.length > 8) {
            return words.slice(0, 8).join(' '); // Take only the first 6 words and rejoin them
          }
          return trimmedTopic; // Return the original trimmed topic if it has 6 or fewer words
        })
        // console.log('allTopics2', allTopicstrimmed);
        // Create an array to store all search results
        let allArticles = [];
        // console.log('think true allTopicsTrimmed', allTopicstrimmed)
        // Determine how many topics to process (up to 1, or fewer if there aren't enough topics)
        const topicsToProcess = Math.min(22, allTopicstrimmed.length); // Originally 1, as per your code
        // Process each of the first few topics sequentially
        for (let i = 0; i < topicsToProcess; i++) {
          if (signal?.aborted) {
            // console.error('[mainWorkflow] Search process aborted at topic index', i);
            throw new Error('Search process aborted');
          }

          const currentTopic = allTopicstrimmed[i];
          const searchQuery = currentTopic;

          if (!searchQuery || searchQuery.trim().length === 0) {
            // console.warn('[mainWorkflow] Empty search query at index', i, ', skipping');
            continue;
          }

          try {
            // console.log('[mainWorkflow] Searching for topic:', searchQuery);
            setLoadingarticles(true);

            // Check for API endpoint in environment variables
            const apiEndpoint = getApiEndpoint();
            // console.log('[mainWorkflow] Using API endpoint:', apiEndpoint);

            // console.log('[mainWorkflow] Sending search request for:', searchQuery);
            const response = await fetch(`${apiEndpoint}/api/search`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                query: searchQuery,
                includeScrapedContent: true,
                options: {
                  numResults: 1,
                  timeframe: 'any_time',
                  source: 'nws',
                  includePaywalled: false
                }
              }),
              signal
            });

            if (!response.ok) {
              // console.error('[mainWorkflow] Search API returned error status:', response.status);
              // console.error('[mainWorkflow] Response status text:', response.statusText);
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const results = await response.json();
            // console.log('[mainWorkflow] Search results received, count:', results.results ? results.results.length : 0);

            if (results.results && results.results.length > 0) {
              // Fixed syntax error in map function and typo in 'souce' to 'source'
              const topicArticles = results.results.map(item => ({
                title: item.title,
                snippet: item.snippet || '',
                link: item.link,
                source: item.source || 'News Source', // Corrected typo from 'souce'
                date: item.date || new Date().toLocaleDateString(),
                // mainContent: item.scrapedContent?.mainContent || null, // Optional chaining for safety
                metaDescription: item.scrapedContent?.metaDescription || null
              }));

              // console.log('[mainWorkflow] Processed', topicArticles.length, 'articles for topic:', searchQuery);
              allArticles = [...allArticles, ...topicArticles];
            } else {
              // console.log('[mainWorkflow] No results found for topic:', searchQuery);
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              // console.error('[mainWorkflow] Search aborted:', error.message);
              throw error;
            }
            // Handle errors but continue with other topics if possible
            // console.error(`[mainWorkflow] Error fetching articles for topic "${currentTopic}":`, error.message);
            // console.error('[mainWorkflow] Error stack:', error.stack);
            // console.error('[mainWorkflow] Error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
          }
        }

        setLoadingarticles(false);
        // console.log('[mainWorkflow] Total articles fetched:', allArticles.length);
        return allArticles;
      };


      // Execute the fetch articles function
      try {
        // console.log('[mainWorkflow] Starting article fetching process');
        fetchedArticles = await fetchArticles();

        if (signal?.aborted) {
          // console.error('[mainWorkflow] Articles generation aborted after fetching');
          throw new Error('Articles generation aborted');
        }

        // console.log('[mainWorkflow] Setting articles hook with:', fetchedArticles);


        setArticleshook(fetchedArticles);

        setThinkingobj((prevState) => {
          const newObj = { ...prevState, articles: fetchedArticles };
          setChatSections((prevChats) => {
            const updatedChats = patchOrAppendChatSections(prevChats, newObj, { articles: fetchedArticles });
            return updatedChats;
          });
          return newObj;
        });
      } catch (error) {
        if (error.name === 'AbortError') {
          // console.error('[mainWorkflow] Article fetching aborted:', error);
          throw error;
        } else {
          // console.error('[mainWorkflow] Error during article fetching:', error);
          // console.error('[mainWorkflow] Article fetch error stack:', error.stack);
          // console.error('[mainWorkflow] Article fetch error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
          setErrorarticles(error.message);
        }
      }
    }
    if (!Think && Searchweb && subtopics) {
      // console.log('think false=>', Think)
      setThinkingobj((prevState) => {
        const newObj = { ...prevState, stage: 'Started Mining Web...', subtopics: subtopics, loading: true, timout_err: false };
        setChatSections((prevChats) => {
          return patchOrAppendChatSections(prevChats, newObj, { stage: 'Started Mining Web...', subtopics: subtopics, loading: true, timout_err: false });
        });
        return newObj;
      });
      const fetchArticles = async () => {
        // Parse subtopics from the string, split by comma, and trim whitespace
        // console.log('think false subtopics=>', subtopics)
        const allTopicsStr = subtopics;
        const allTopics = allTopicsStr.split('#').map(topic => topic.trim()).filter(topic => topic.length > 0).slice(3, allTopicsStr?.length)
        const allTopicstrimmed = allTopics.map(topic => {
          const trimmedTopic = topic.trim();
          const words = trimmedTopic.split(/\s+/); // Split by whitespace
          if (words.length > 8) {
            return words.slice(0, 8).join(' '); // Take only the first 6 words and rejoin them
          }
          return trimmedTopic; // Return the original trimmed topic if it has 6 or fewer words
        })
        // console.log('allTopics2', allTopicstrimmed);
        // Create an array to store all search results
        let allArticles = [];
        // console.log('think false allTopicsTrimmed', allTopicstrimmed)
        // Determine how many topics to process (up to 9, or fewer if there aren't enough topics)
        const topicsToProcess = Math.min(15, allTopicstrimmed.length);
        // Process each topic sequentially
        for (let i = 0; i < topicsToProcess; i++) {
          if (signal?.aborted) {
            throw new Error('Search process aborted');
          }

          const currentTopic = allTopicstrimmed[i];
          const searchQuery = currentTopic;

          if (!searchQuery || searchQuery.trim().length === 0) {
            // console.warn('[mainWorkflow] Empty search query at index', i, ', skipping');
            continue;
          }

          try {
            setLoadingarticles(true);

            // Check for API endpoint in environment variables
            const apiEndpoint = getApiEndpoint();

            const response = await fetch(`${apiEndpoint}/api/search`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                query: searchQuery,
                includeScrapedContent: true,
                options: {
                  numResults: 1,
                  timeframe: 'any_time',
                  source: 'nws',
                  includePaywalled: false
                }
              }),
              signal
            });

            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`);
            }

            const results = await response.json();

            if (results.results && results.results.length > 0) {
              const topicArticles = results.results.map(item => ({
                title: item.title,
                snippet: item.snippet || '',
                link: item.link,
                source: item.source || 'News Source',
                date: item.date || new Date().toLocaleDateString(),
                // mainContent: item.scrapedContent?.mainContent || null,
                metaDescription: item.scrapedContent?.metaDescription || null
              }));

              allArticles = [...allArticles, ...topicArticles];
            }
          } catch (error) {
            if (error.name === 'AbortError') {
              throw error;
            }
            // Handle errors but continue with other topics if possible
          }
        }

        setLoadingarticles(false);
        return allArticles;

      };
      // Execute the fetch articles function
      try {
        // console.log('[mainWorkflow] Starting article fetching process');
        fetchedArticles = await fetchArticles();

        if (signal?.aborted) {
          // console.error('[mainWorkflow] Articles generation aborted after fetching');
          throw new Error('Articles generation aborted');
        }

        // console.log('[mainWorkflow] Setting articles hook with:', fetchedArticles);


        setArticleshook(fetchedArticles);

        setThinkingobj((prevState) => {
          const newObj = { ...prevState, articles: fetchedArticles };
          setChatSections((prevChats) => {
            const updatedChats = patchOrAppendChatSections(prevChats, newObj, { articles: fetchedArticles });
            return updatedChats;
          });
          return newObj;
        });
      } catch (error) {
        if (error.name === 'AbortError') {
          // console.error('[mainWorkflow] Article fetching aborted:', error);
          throw error;
        } else {
          // console.error('[mainWorkflow] Error during article fetching:', error);
          // console.error('[mainWorkflow] Article fetch error stack:', error.stack);
          // console.error('[mainWorkflow] Article fetch error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
          setErrorarticles(error.message);
        }
      }
      // console.log('[mainWorkflow] Skipping web search (Searchweb=false)');
    }
    // Somewhere in your component

    setThinkingobj((prevState) => {
      const newObj = { ...prevState, stage: 'Ended Mining Web', subtopics: subtopics, loading: true, timout_err: false };
      setChatSections((prevChats) => {
        return patchOrAppendChatSections(prevChats, newObj, { stage: 'Ended Mining Web', subtopics: subtopics, loading: true, timout_err: false });
      });
      return newObj;
    });

    if (signal?.aborted) {
      // console.error('[mainWorkflow] Workflow aborted after web mining');
      setThinkingobj((prevState) => {
        const newObj = { ...prevState, error: true, loading: false, timout_err: true };
        setChatSections((prev) => {
          return patchOrAppendChatSections(prev, newObj, { error: true, loading: false, timout_err: true });
        });
        return newObj;
      });
      throw new Error('Articles generation aborted');
    }

    // 3) Actor-Critic Analysis
    try {
      loopCount = parseInt(localStorage.getItem('loops') || '0');
      // console.log('[mainWorkflow] Starting Actor-Critic Analysis with loop count:', loopCount);
    } catch (error) {
      // console.error('[mainWorkflow] Error getting loop count from localStorage:', error);
      // console.error('[mainWorkflow] Loop count error details before analysis:', error.message);
    }

    try {
      // console.log('[mainWorkflow] Initializing ActorCriticAnalysis with', fetchedArticles.length, 'articles');
      const analysis = ActorCriticAnalysis({
        subtopics,
        articleshook: fetchedArticles,
        DeepSearch,
        loopCount,
        question: input,
        setThinkingobj,
        setIter1Hook,
        setFinalSummaryhook,
        chatSections,
        setChatSections,
        setAPIError,
        signal,

        setIsLoading,
        apiError,
        Actor_Model_apiProvider, setActor_Model_apiProvider,
        Critic_Model_apiProvider, setCritic_Model_apiProvider,
        Actor_Model_apiModel, setActor_Model_apiModel,
        Critic_Model_apiModel, setCritic_Model_apiModel,
        Actor_Model_apikey, setActor_Model_apikey,
        Critic_Model_apikey, setCritic_Model_apikey,
        temperature_val_Actor_Model_apiProvider, setTemperature_val_Actor_Model_apiProvider,
        temperature_val_Critic_Model_apiProvider, setTemperature_val_Critic_Model_apiProvider
      });

      // console.log('[mainWorkflow] Running actorCriticAnalysis');
      await analysis.actorCriticAnalysis();
      // console.log('[mainWorkflow] Completed actorCriticAnalysis successfully');
    } catch (error) {
      // console.error('[mainWorkflow] Error during Actor-Critic analysis:', error);
      // console.error('[mainWorkflow] Analysis error stack:', error.stack);
      // console.error('[mainWorkflow] Analysis error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      throw error; // Re-throw to be caught by the outer try-catch
    }

    if (signal?.aborted) {
      // console.error('[mainWorkflow] Workflow aborted after analysis');
      setThinkingobj((prevState) => {
        const newObj = { ...prevState, error: true, loading: false, timout_err: true };
        setChatSections((prev) => {
          return patchOrAppendChatSections(prev, newObj, { error: true, loading: false, timout_err: true });
        });
        return newObj;
      });
      throw new Error('Analysis aborted');
    }

    // 4) Mark thinkingobj as completed
    // console.log('[mainWorkflow] Workflow completed successfully');
    setThinkingobj((prevState) => {
      const newObj = { ...prevState, stage: 'Completed', loading: false, timout_err: false };
      setChatSections((prevChats) => {
        return patchOrAppendChatSections(prevChats, newObj, { stage: 'Completed', loading: false, timout_err: false });
      });
      return newObj;
    });

    setIsLoading(false);
  } catch (error) {
    // Handle all errors in one place
    // console.error('[mainWorkflow] Caught error in main try-catch block:', error.message);
    // console.error('[mainWorkflow] Error type:', error.name);
    // console.error('[mainWorkflow] Error stack:', error.stack);
    // console.error('[mainWorkflow] Full error details:', JSON.stringify(error, Object.getOwnPropertyNames(error)));

    setThinkingobj((prevState) => {
      const newObj = {
        ...prevState,
        error: true,
        loading: false,
        timout_err: error.name === 'AbortError' ? true : false,
        errorMessage: error.message
      };
      setChatSections((prev) => {
        return patchOrAppendChatSections(prev, newObj, {
          error: true,
          loading: false,
          timout_err: error.name === 'AbortError' ? true : false,
          errorMessage: error.message
        });
      });
      return newObj;
    });

    // console.error('[mainWorkflow] Error:', error.message);
    setAPIError(error.message);
    setIsLoading(false);
  }
};

// Helper function to get API endpoint
function getApiEndpoint() {
  // Check for environment variables first (for production)
  if (typeof process !== 'undefined' && process.env && process.env.SERP_PROXY_API_URL) {
    // console.log('[getApiEndpoint] Using SERP_PROXY_API_URL from env:', process.env.SERP_PROXY_API_URL);
    return process.env.SERP_PROXY_API_URL;
  }

  // // For local development
  // if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
  //   // console.log('[getApiEndpoint] Using localhost API endpoint');
  //   return 'http://localhost:3001';
  // }

  // Production endpoint for Render.io deployment
  // console.log('[getApiEndpoint] Using production API endpoint on Render.io');
  return 'https://serpapi-proxy-746q.onrender.com';
}

export default mainWorkflow;